/**
 * CredentialStore implementation backed by auth.json.
 * Provider auth orchestration belongs to ModelRuntime and pi-ai Models.
 */

import {
	type AuthOperationOptions,
	type Credential,
	type CredentialInfo,
	type CredentialStore,
	normalizeProviderId,
} from "@bastani/pi-ai";
import { join } from "path";
import { isDeepStrictEqual } from "util";
import { getAgentConfigPaths, getAgentDir } from "../config.js";
import { raceWithAbortSignal } from "../utils/abort.js";
import { stripBom } from "../utils/text.ts";
import {
	type AuthStorageBackend,
	FileAuthStorageBackend,
	InMemoryAuthStorageBackend,
} from "./auth-storage-backends.ts";
import { LEGACY_AZURE_PROVIDER, normalizeProviderKeys } from "./azure-provider-compat.js";
import { resolveConfigValue } from "./resolve-config-value.ts";

export {
	type AuthStorageBackend,
	FileAuthStorageBackend,
	InMemoryAuthStorageBackend,
} from "./auth-storage-backends.ts";

export type AuthStorageData = Record<string, Credential>;

export class AuthStorage implements CredentialStore {
	private data: AuthStorageData = {};
	private loadedVersion: string | undefined;
	private storage: AuthStorageBackend;

	private constructor(storage: AuthStorageBackend) {
		this.storage = storage;
		this.reload();
	}

	static create(authPath?: string | string[]): AuthStorage {
		const paths =
			authPath === undefined ? getAgentConfigPaths("auth.json") : Array.isArray(authPath) ? authPath : [authPath];
		return new AuthStorage(new FileAuthStorageBackend(paths[0] ?? join(getAgentDir(), "auth.json"), paths));
	}

	static fromStorage(storage: AuthStorageBackend): AuthStorage {
		return new AuthStorage(storage);
	}

	static inMemory(data: AuthStorageData = {}): AuthStorage {
		const storage = new InMemoryAuthStorageBackend();
		storage.withLock(() => ({ result: undefined, next: JSON.stringify(data, null, 2) }));
		return AuthStorage.fromStorage(storage);
	}

	private parseStorageData(content: string | undefined): AuthStorageData {
		if (!content) {
			return {};
		}
		return normalizeProviderKeys(JSON.parse(stripBom(content)) as AuthStorageData);
	}

	private readStorageContent(): string | undefined {
		if (this.storage.read) return this.storage.read();
		let content: string | undefined;
		this.storage.withLock((current) => {
			content = current;
			return { result: undefined };
		});
		return content;
	}

	/**
	 * Reload credentials from storage.
	 */
	reload(): void {
		this.loadFromStorage(this.readStorageVersion());
	}

	reloadIfChanged(): boolean {
		if (!this.storage.version && !this.storage.read) return false;
		const version = this.readStorageVersion();
		if (version !== undefined && version === this.loadedVersion) return false;
		return this.loadFromStorage(version);
	}

	private readStorageVersion(): string | undefined {
		try {
			return this.storage.version?.();
		} catch {
			return undefined;
		}
	}

	private loadFromStorage(version: string | undefined): boolean {
		try {
			const next = this.parseStorageData(this.readStorageContent());
			this.loadedVersion = version;
			if (isDeepStrictEqual(next, this.data)) return false;
			this.data = next;
			return true;
		} catch (error) {
			if (error instanceof SyntaxError) this.loadedVersion = version;
			return false;
		}
	}

	/** Read the current in-memory credential snapshot without triggering refresh or I/O. */
	peek(provider: string): Credential | undefined {
		return this.data[normalizeProviderId(provider)];
	}

	async read(provider: string, options?: AuthOperationOptions): Promise<Credential | undefined> {
		options?.signal?.throwIfAborted();
		this.reloadIfChanged();
		const credential = this.peek(provider);
		if (credential?.type !== "api_key") return credential;
		if (credential.key === undefined) return credential;
		return { ...credential, key: resolveConfigValue(credential.key, credential.env) };
	}

	async modify(
		provider: string,
		fn: (current: Credential | undefined) => Promise<Credential | undefined>,
		options?: AuthOperationOptions,
	): Promise<Credential | undefined> {
		provider = normalizeProviderId(provider);
		const signal = options?.signal;
		signal?.throwIfAborted();
		if (this.storage.modifyProviderAsync) {
			const operation = this.storage.modifyProviderAsync(
				provider,
				async (current) => {
					signal?.throwIfAborted();
					const pending = fn(current);
					const next = signal === undefined ? await pending : await raceWithAbortSignal(pending, signal);
					signal?.throwIfAborted();
					return next;
				},
				signal,
			);
			const snapshot = signal === undefined ? await operation : await raceWithAbortSignal(operation, signal);
			this.data = this.parseStorageData(snapshot.next);
			if (snapshot.result !== undefined) this.data[provider] = snapshot.result;
			this.loadedVersion = undefined;
			return snapshot.result;
		}
		let persistedData: AuthStorageData | undefined;
		const operation = this.storage.withLockAsync(async (content) => {
			signal?.throwIfAborted();
			const currentData = this.parseStorageData(content);
			const pending = fn(currentData[provider]);
			const next = signal === undefined ? await pending : await raceWithAbortSignal(pending, signal);
			signal?.throwIfAborted();
			if (next === undefined) {
				persistedData = currentData;
				return { result: currentData[provider] };
			}

			const merged: AuthStorageData = { ...currentData, [provider]: next };
			persistedData = merged;
			return { result: next, next: JSON.stringify(merged, null, 2) };
		});
		const result = signal === undefined ? await operation : await raceWithAbortSignal(operation, signal);
		if (persistedData) this.data = persistedData;
		this.loadedVersion = undefined;
		return result;
	}

	async delete(provider: string): Promise<void> {
		provider = normalizeProviderId(provider);
		if (this.storage.deleteProviderAsync) {
			if (provider === "azure") await this.storage.deleteProviderAsync(LEGACY_AZURE_PROVIDER);
			const content = await this.storage.deleteProviderAsync(provider);
			this.data = this.parseStorageData(content);
			this.loadedVersion = undefined;
			return;
		}
		let persistedData: AuthStorageData | undefined;
		await this.storage.withLockAsync(async (content) => {
			const currentData = this.parseStorageData(content);
			delete currentData[provider];
			persistedData = currentData;
			return { result: undefined, next: JSON.stringify(currentData, null, 2) };
		});
		if (persistedData) this.data = persistedData;
		this.loadedVersion = undefined;
	}

	/** List credential metadata without resolving configured key values. */
	async list(options?: AuthOperationOptions): Promise<readonly CredentialInfo[]> {
		options?.signal?.throwIfAborted();
		this.reloadIfChanged();
		return Object.entries(this.data).map(([providerId, credential]) => ({ providerId, type: credential.type }));
	}
}

/**
 * Read credentials without acquiring a lock, creating auth.json, or allowing a
 * refresh to persist a mutation. `auth check --no-refresh` uses this store.
 * It resolves configured key values, including `!command`, like `AuthStorage`.
 * Unlike `AuthStorage.reload()`, malformed data fails closed so the probe can
 * report invalid auth state instead of silently treating it as no credentials.
 */
export class ReadOnlyAuthStorage implements CredentialStore {
	private data: AuthStorageData | undefined;
	private readonly storage: FileAuthStorageBackend;

	constructor(authPath?: string | string[]) {
		const paths =
			authPath === undefined ? getAgentConfigPaths("auth.json") : Array.isArray(authPath) ? authPath : [authPath];
		this.storage = new FileAuthStorageBackend(paths[0] ?? join(getAgentDir(), "auth.json"), paths);
	}

	private load(): AuthStorageData {
		if (this.data !== undefined) return this.data;

		let parsed: unknown;
		try {
			const content = this.storage.read();
			parsed = content === undefined ? {} : JSON.parse(stripBom(content));
		} catch (error) {
			// The no-refresh path must not turn a corrupt auth file into an empty
			// credential set. Surface invalid state without touching the file.
			throw new Error(`Failed to read auth.json: ${error instanceof Error ? error.message : String(error)}`);
		}

		if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
			throw new Error("Invalid auth.json: expected an object");
		}
		for (const [providerId, credential] of Object.entries(parsed)) {
			if (typeof credential !== "object" || credential === null || Array.isArray(credential)) {
				throw new Error(`Invalid auth.json credential for provider "${providerId}"`);
			}
			const value = credential as Record<string, unknown>;
			if (value.type === "api_key") {
				const env = value.env;
				const validKey = value.key === undefined || typeof value.key === "string";
				const validEnv =
					env === undefined ||
					(typeof env === "object" &&
						env !== null &&
						!Array.isArray(env) &&
						Object.values(env).every((entry) => typeof entry === "string"));
				if (validKey && validEnv) continue;
			} else if (
				value.type === "oauth" &&
				typeof value.access === "string" &&
				typeof value.refresh === "string" &&
				typeof value.expires === "number" &&
				Number.isFinite(value.expires)
			) {
				continue;
			}
			throw new Error(`Invalid auth.json credential for provider "${providerId}"`);
		}

		this.data = normalizeProviderKeys(parsed as AuthStorageData);
		return this.data;
	}

	/** Raw configured values for privacy screening; never execute API-key commands. */
	peek(providerId: string): Credential | undefined {
		return this.load()[normalizeProviderId(providerId)];
	}

	async read(providerId: string, options?: AuthOperationOptions): Promise<Credential | undefined> {
		options?.signal?.throwIfAborted();
		const credential = this.peek(providerId);
		options?.signal?.throwIfAborted();
		if (!credential) return undefined;
		if (credential.type !== "api_key" || credential.key === undefined) {
			return structuredClone(credential);
		}
		return { ...credential, key: resolveConfigValue(credential.key, credential.env) };
	}

	async list(options?: AuthOperationOptions): Promise<readonly CredentialInfo[]> {
		options?.signal?.throwIfAborted();
		const credentials = Object.entries(this.load()).map(([providerId, credential]) => ({
			providerId,
			type: credential.type,
		}));
		options?.signal?.throwIfAborted();
		return credentials;
	}

	async modify(
		_providerId: string,
		_fn: (current: Credential | undefined) => Promise<Credential | undefined>,
		_options?: AuthOperationOptions,
	): Promise<Credential | undefined> {
		throw new Error("Read-only credential storage cannot modify auth.json");
	}

	async delete(_providerId: string, _options?: AuthOperationOptions): Promise<void> {
		throw new Error("Read-only credential storage cannot modify auth.json");
	}
}

/** Read one persisted provider credential using Atomic's layered auth.json paths. */
export function readStoredCredential(providerId: string, authPath?: string | string[]): Credential | undefined {
	const paths =
		authPath === undefined ? getAgentConfigPaths("auth.json") : Array.isArray(authPath) ? authPath : [authPath];
	const storage = new FileAuthStorageBackend(paths[0] ?? join(getAgentDir(), "auth.json"), paths);
	try {
		let credential: Credential | undefined;
		storage.withLock((content) => {
			credential = content
				? normalizeProviderKeys(JSON.parse(stripBom(content)) as AuthStorageData)[normalizeProviderId(providerId)]
				: undefined;
			return { result: undefined };
		});
		return credential;
	} catch {
		return undefined;
	}
}
